import { tx as translateInterfaceText } from '@/utils/i18n';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ProtectionBadge } from '@/components/security/shared/ProtectionBadge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Mail, Lock, BarChart3, Plug, Plus, UserPlus } from 'lucide-react';
import { VSLeadsStrategyList, Strategy } from '@/components/vsleads/VSLeadsStrategyList';
import { VSLeadsReportsTab } from '@/components/vsleads/reports/VSLeadsReportsTab';
import { VSLeadsPageHeader } from '@/components/vsleads/VSLeadsPageHeader';
import { VSLeadsIntegrations } from '@/components/vsleads/VSLeadsIntegrations';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useVSLeadsVideos } from '@/hooks/useVSLeadsVideos';
import { t } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

export default function VSLeads() {
  useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') || 'captura-email';
  });
  const [selectedVideoIdForReports, setSelectedVideoIdForReports] = useState<string | null>(null);
  const { toast } = useToast();

  // Atualizar tab ativa quando o parâmetro da URL mudar
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Buscar vídeos com captura de leads ativa da API
  const { videos: vsLeadsVideos, isLoading, error, refetch } = useVSLeadsVideos(user?.id || null);

  // Converter vídeos para o formato Strategy esperado pelo componente
  const strategies: Strategy[] = vsLeadsVideos.map(video => ({
    id: video.id,
    videoId: video.videoId,
    videoTitle: video.videoTitle,
    videoThumbnail: video.videoThumbnail || undefined,
    tipo: video.tipo,
    createdAt: video.createdAt,
    status: video.status,
    totalViews: video.totalViews,
  }));

  useEffect(() => {
    if (error) {
      toast({
        title: t('loadVideosError'),
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);
  
  const handleCreateNew = () => {
    // Navegar baseado na aba ativa
    if (activeTab === 'captura-senha') {
      navigate('/vsleads/create?tipo=captura-senha');
    } else {
      navigate('/vsleads/email-capture');
    }
  };
  
  const handleUpdateStrategy = async (id: string, updates: Partial<Strategy>) => {
    // Recarregar vídeos após atualização
    await refetch();

    toast({
      title: t('strategyUpdatedToast'),
      description: t('strategyUpdatedSuccessMsg'),
    });
  };

  const handleDeleteStrategy = async (id: string) => {
    // Recarregar vídeos após exclusão
    await refetch();

    toast({
      title: t('strategyRemovedToast'),
      description: t('strategyUpdatedSuccessMsg'),
    });
  };

  const handleNavigateToReports = (videoId: string) => {
    setSelectedVideoIdForReports(videoId);
    setActiveTab('relatorios');
  };

  const handleNavigateToIntegrations = () => {
    setActiveTab('integracoes');
  };
  
  return (
    <Layout fixedSidebar={true}>
      <div className="flex-1 flex flex-col overflow-hidden bg-background">
        {/* Header Premium */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 md:pt-4 pb-4 md:pb-5">
            <div className="flex items-center gap-2.5">
              <Mail className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} />
              <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
                {t('leadCaptureTitle')}
              </h1>
            </div>
          </div>
          <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)', marginTop: '1px' }} />
        </div>

        <main className="flex-1 overflow-y-auto scrollbar-blue feed-scroll-container">
          <div className="p-6 space-y-6">
            <Tabs
              defaultValue={activeTab} 
              value={activeTab}
              onValueChange={setActiveTab} 
              className="w-full"
            >
            <div className="overflow-x-auto pb-2 -mx-2 px-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <TabsList className="flex gap-2 bg-transparent p-0 h-[44px] min-w-max">
                <TabsTrigger 
                  value="captura-email"
                  className="h-[44px] px-5 rounded-[10px] text-[14px] font-medium transition-all
                             data-[state=active]:bg-[#2F6FED] data-[state=active]:text-white data-[state=active]:font-semibold
                             data-[state=inactive]:bg-[#F5F7FA] data-[state=inactive]:text-[#4B5563]
                             hover:opacity-90 whitespace-nowrap"
                >
                  {t('captureWithEmailLabel')}
                </TabsTrigger>

                <TabsTrigger
                  value="captura-senha"
                  className="h-[44px] px-5 rounded-[10px] text-[14px] font-medium transition-all
                             data-[state=active]:bg-[#2F6FED] data-[state=active]:text-white data-[state=active]:font-semibold
                             data-[state=inactive]:bg-[#F5F7FA] data-[state=inactive]:text-[#4B5563]
                             hover:opacity-90 whitespace-nowrap"
                >
                  {t('captureWithPasswordLabel')}
                </TabsTrigger>

                <TabsTrigger
                  value="relatorios"
                  className="h-[44px] px-5 rounded-[10px] text-[14px] font-medium transition-all
                             data-[state=active]:bg-[#2F6FED] data-[state=active]:text-white data-[state=active]:font-semibold
                             data-[state=inactive]:bg-[#F5F7FA] data-[state=inactive]:text-[#4B5563]
                             hover:opacity-90 whitespace-nowrap"
                >
                  {translateInterfaceText("Relatórios")}</TabsTrigger>

                <TabsTrigger
                  value="integracoes"
                  className="h-[44px] px-5 rounded-[10px] text-[14px] font-medium transition-all
                             data-[state=active]:bg-[#2F6FED] data-[state=active]:text-white data-[state=active]:font-semibold
                             data-[state=inactive]:bg-[#F5F7FA] data-[state=inactive]:text-[#4B5563]
                             hover:opacity-90 whitespace-nowrap"
                >
                  {t('integrações')}
                </TabsTrigger>
              </TabsList>
            </div>

                <TabsContent value="captura-email" className="pt-2 animate-fade-in">
                  {isLoading ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-12 text-center">
                      <p className="text-gray-500">{t('loadingStrategies')}</p>
                    </div>
                  ) : (
                    <VSLeadsStrategyList 
                      strategies={strategies.filter(s => s.tipo === 'captura-dados')}
                      type="captura-dados"
                      onUpdate={handleUpdateStrategy}
                      onDelete={handleDeleteStrategy}
                      onNavigateToReports={handleNavigateToReports}
                      onNavigateToIntegrations={handleNavigateToIntegrations}
                      onCreateNew={handleCreateNew}
                    />
                  )}
                </TabsContent>

                <TabsContent value="captura-senha" className="pt-2 animate-fade-in">
                  {isLoading ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-12 text-center">
                      <p className="text-gray-500">{t('loadingStrategies')}</p>
                    </div>
                  ) : (
                    <VSLeadsStrategyList 
                      strategies={strategies.filter(s => s.tipo === 'captura-senha')}
                      type="captura-senha"
                      onUpdate={handleUpdateStrategy}
                      onDelete={handleDeleteStrategy}
                      onNavigateToReports={handleNavigateToReports}
                      onNavigateToIntegrations={handleNavigateToIntegrations}
                      onCreateNew={handleCreateNew}
                    />
                  )}
                </TabsContent>

              <TabsContent value="relatorios" className="pt-2 animate-fade-in">
                <VSLeadsReportsTab preselectedVideoId={selectedVideoIdForReports} />
              </TabsContent>

              <TabsContent value="integracoes" className="pt-2 animate-fade-in">
                <VSLeadsIntegrations />
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </Layout>
  );
}
